import { MIN_RSA_MODULUS_BITS } from "src/oauthClients/constants";
import {
  generateClientKeyPair,
  KeyMaterialError,
  PublicJwk,
  publicJwkFromJwkJson,
  publicJwkFromSpkiPem,
  rsaModulusBits,
} from "src/oauthClients/keyMaterial";

export type KeySupplyMode = "generate" | "provide";

export type ResolvedKeyMaterial = {
  publicJwk: PublicJwk;
  /** Only set on the generate path — this is the one copy of the private key. */
  privateKeyPem?: string;
};

/**
 * Turns whichever key the customer supplied into the public JWK the management
 * API takes. Kept out of keyMaterial.ts so the primitives it calls stay
 * separately mockable, and out of the fieldset so both dialogs can reach it
 * without importing a component.
 */
export const resolveKeyMaterial = async (
  mode: KeySupplyMode,
  pastedKey: string,
): Promise<ResolvedKeyMaterial> => {
  if (mode === "generate") {
    return generateClientKeyPair();
  }

  const trimmed = pastedKey.trim();
  if (!trimmed) {
    throw new KeyMaterialError("Paste a public key first");
  }
  const publicJwk = trimmed.startsWith("{")
    ? await publicJwkFromJwkJson(trimmed)
    : await publicJwkFromSpkiPem(trimmed);

  if (rsaModulusBits(publicJwk) < MIN_RSA_MODULUS_BITS) {
    throw new KeyMaterialError(
      `The RSA modulus must be at least ${MIN_RSA_MODULUS_BITS} bits`,
    );
  }
  return { publicJwk };
};
